import React, { useState, useEffect } from 'react';
import {
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Tag,
  Typography,
  Row,
  Col,
} from 'antd';
import { EditOutlined, GiftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Api from '../../Network';
import { LoyaltyPoint } from '../../types/types';
import { usePermissions } from '../../contexts/PermissionContext';
import MobileTable from '../../Component/MobileTable';
import { AnimatedContainer, PageHeader, StatCard, SectionCard } from '../../Component/ui';
import './style.scss';

const { Text } = Typography;
const { Option } = Select;

const LoyaltyPoints: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const [form] = Form.useForm();

  const [loyaltyPoints, setLoyaltyPoints] = useState<LoyaltyPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState<LoyaltyPoint | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Fetch loyalty points data
  const fetchLoyaltyPoints = async () => {
    setLoading(true);
    try {
      const response: any = await Api.get('/api/admin/loyalty');
      if (response.status && response.data) {
        setLoyaltyPoints(response.data);
      }
    } catch (error) {
      message.error(t('messages.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoyaltyPoints();
  }, []);

  // Handle form submission
  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    try {
      if (editingRule) {
        // Update existing rule
        await Api.post(`/api/admin/loyalty`, values);
        message.success(t('loyaltyPoints.ruleUpdated'));
      } else {
        // Create new rule
        await Api.post('/api/admin/loyalty', values);
        message.success(t('loyaltyPoints.ruleAdded'));
      }

      setModalVisible(false);
      setEditingRule(null);
      form.resetFields();
      fetchLoyaltyPoints();
    } catch (error) {
      message.error(t('messages.operationFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit
  const handleEdit = (record: LoyaltyPoint) => {
    setEditingRule(record);
    form.setFieldsValue({
      customer_type: record.customer_type,
      points: record.points.toString(),
      discount_amount: record.discount_amount,
      status: record.status,
      customer_category_id: String(record.customer_category_id),
    });
    setModalVisible(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalVisible(false);
    setEditingRule(null);
    form.resetFields();
  };

  // Calculate statistics
  const activeRules = loyaltyPoints.filter((rule) => rule.status === 'active').length;
  const totalRules = loyaltyPoints.length;

  // Table columns
  const columns = [
    {
      title: t('loyaltyPoints.columns.customerType'),
      dataIndex: 'customer_type',
      key: 'customer_type',
      render: (type: string) => (
        <Tag color={type === 'credit' ? 'blue' : 'green'}>{t(`loyaltyPoints.${type}`)}</Tag>
      ),
    },
    {
      title: t('loyaltyPoints.columns.customerCategory'),
      dataIndex: ['customer_category', 'name_en'],
      key: 'customer_category',
      render: (name: string, record: LoyaltyPoint) => (
        <div>
          <div>{name}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.customer_category.name_ar}
          </Text>
        </div>
      ),
    },
    {
      title: t('loyaltyPoints.columns.points'),
      dataIndex: 'points',
      key: 'points',
      render: (points: number) => (
        <Tag color="orange">
          <GiftOutlined /> {points} {t('loyaltyPoints.points')}
        </Tag>
      ),
    },
    {
      title: t('loyaltyPoints.columns.discountAmount'),
      dataIndex: 'discount_amount',
      key: 'discount_amount',
      render: (amount: string) => (
        <Text strong>
          {amount} {t('common.SAR')}
        </Text>
      ),
    },
    {
      title: t('loyaltyPoints.columns.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'success' : 'default'}>
          {t(`loyaltyPoints.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('loyaltyPoints.columns.actions'),
      key: 'actions',
      render: (_: any, record: LoyaltyPoint) => (
        <Space>
          {hasPermission('update_loyalty_points') && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            >
              {t('common.edit')}
            </Button>
          )}
          {/* <Popconfirm
            title={t('loyaltyPoints.deleteConfirm')}
            description={t('loyaltyPoints.deleteWarning')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              {t('common.delete')}
            </Button>
          </Popconfirm> */}
        </Space>
      ),
    },
  ];

  return (
    <AnimatedContainer className="loyalty-points-page" variant="fadeUp">
      <PageHeader
        icon={<GiftOutlined />}
        title={t('loyaltyPoints.title')}
        subtitle={t('loyaltyPoints.subtitle')}
      />

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <StatCard
            tone="success"
            icon={<GiftOutlined />}
            title={t('loyaltyPoints.active')}
            value={`${activeRules} / ${totalRules}`}
          />
        </Col>
        {/* <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('loyaltyPoints.points')}
              value={averagePoints}
              suffix={t('loyaltyPoints.points')}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title={t('common.total')}
              value={totalRules}
              suffix={t('loyaltyPoints.title')}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col> */}
      </Row>

      {/* Main Content */}
      <SectionCard>
        {/* <div className="table-header">
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setModalVisible(true)}
            >
              {t('loyaltyPoints.addNew')}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchLoyaltyPoints}
              loading={loading}
            >
              {t('common.refresh')}
            </Button>
          </Space>
        </div> */}

        <MobileTable
          columns={columns}
          dataSource={loyaltyPoints}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total: number, range: [number, number]) =>
              `${t('table.showing')} ${range[0]}-${range[1]} ${t('table.of')} ${total} ${t('table.entries')}`,
          }}
          locale={{
            emptyText: t('loyaltyPoints.noData'),
          }}
        />
      </SectionCard>

      {/* Add/Edit Modal */}
      <Modal
        title={editingRule ? t('loyaltyPoints.editRule') : t('loyaltyPoints.addNew')}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            status: 'active',
          }}
        >
          <Row gutter={16}>
            <Col span={12} style={{ visibility: 'hidden' }}>
              <Form.Item
                name="customer_type"
                label={t('loyaltyPoints.form.customerType')}
                rules={[{ required: true, message: t('validation.required') }]}
              >
                <Select
                  placeholder={t('loyaltyPoints.form.customerTypePlaceholder')}
                  disabled={true}
                >
                  <Option value="credit">{t('loyaltyPoints.credit')}</Option>
                  <Option value="cash">{t('loyaltyPoints.cash')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12} style={{ visibility: 'hidden' }}>
              <Form.Item
                name="customer_category_id"
                label={t('loyaltyPoints.form.customerCategory')}
                rules={[{ required: true, message: t('validation.required') }]}
              >
                <Select
                  placeholder={t('loyaltyPoints.form.customerCategoryPlaceholder')}
                  disabled={true}
                >
                  <Option value={1}>Gold / ذهبي</Option>
                  <Option value={2}>Silver / فضي</Option>
                  <Option value={3}>Diamond / ماسي</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="points"
                label={t('loyaltyPoints.form.points')}
                rules={[{ required: true, message: t('validation.required') }]}
              >
                <Input type="number" placeholder={t('loyaltyPoints.form.pointsPlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="discount_amount"
                label={t('loyaltyPoints.form.discountAmount')}
                rules={[{ required: true, message: t('validation.required') }]}
              >
                <Input
                  type="number"
                  step="0.01"
                  placeholder={t('loyaltyPoints.form.discountAmountPlaceholder')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="status"
                label={t('loyaltyPoints.form.status')}
                rules={[{ required: true, message: t('validation.required') }]}
              >
                <Select placeholder={t('loyaltyPoints.form.statusPlaceholder')}>
                  <Option value="active">{t('loyaltyPoints.active')}</Option>
                  <Option value="inactive">{t('loyaltyPoints.inactive')}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleModalClose}>{t('loyaltyPoints.form.cancel')}</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {editingRule ? t('loyaltyPoints.updateRule') : t('loyaltyPoints.addRule')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </AnimatedContainer>
  );
};

export default LoyaltyPoints;
